import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Globe2, Save, X, Smartphone, Mail, Send } from "lucide-react";

export default function AllowedCountriesSection() {
  const [countries, setCountries] = useState([]);
  const [input, setInput] = useState("");
  const [alertPhone, setAlertPhone] = useState("");
  const [summaryEmail, setSummaryEmail] = useState("");
  const [savedSummary, setSavedSummary] = useState("");
  const [sendingSummary, setSendingSummary] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/trace-settings").then((r) => { setCountries(r.data.allowed_countries || []); setAlertPhone(r.data.alert_phone || ""); setSummaryEmail(r.data.summary_email || ""); setSavedSummary(r.data.summary_email || ""); }).catch(() => {});
  }, []);

  const add = (e) => {
    e.preventDefault();
    const codes = input.toUpperCase().split(/[\s,;]+/).filter((c) => /^[A-Z]{2}$/.test(c));
    if (!codes.length) {
      toast.error("Use 2-letter country codes, e.g. PT, ES, FR");
      return;
    }
    setCountries((prev) => [...new Set([...prev, ...codes])].sort());
    setInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/trace-settings", { allowed_countries: countries, alert_phone: alertPhone.trim(), summary_email: summaryEmail.trim() });
      setSummaryEmail(data.summary_email || "");
      setSavedSummary(data.summary_email || "");
      setCountries(data.allowed_countries);
      setAlertPhone(data.alert_phone || "");
      toast.success("Traceability settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendSummary = async (kind) => {
    setSendingSummary(true);
    try {
      const { data } = await api.post(kind === "weekly" ? "/admin/weekly-report/send" : "/admin/daily-summary/send");
      const msg = { sent: `Summary sent (${data.scans} scans)`, failed: "Email could not be sent — check the email settings" }[data.status];
      if (data.status === "sent") toast.success(msg);
      else toast.error(msg || `Not sent: ${data.reason}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send summary");
    } finally {
      setSendingSummary(false);
    }
  };

  return (
    <section className="rounded-lg p-8 border space-y-5" style={{ background: "var(--vault-surface)", borderColor: "var(--vault-border)" }} data-testid="allowed-countries-section">
      <div>
        <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-2"><Globe2 className="w-5 h-5" style={{ color: "var(--vault-gold)" }} /> Allowed countries</h2>
        <p className="text-sm text-[var(--vault-text-muted)]">
          NFC scans outside these countries are marked red on the traceability map and trigger an alert to the code's notify email and an SMS to the alert phone. Access is never blocked. Leave empty to disable.
        </p>
      </div>
      <div className="flex flex-wrap gap-2 min-h-[32px]" data-testid="allowed-countries-list">
        {countries.length === 0 && <span className="text-xs text-[var(--vault-text-faint)]">No restriction — all countries allowed.</span>}
        {countries.map((c) => (
          <span key={c} className="code-chip inline-flex items-center gap-1.5" data-testid={`country-chip-${c}`}>
            {c}
            <button type="button" onClick={() => setCountries((p) => p.filter((x) => x !== c))} data-testid={`country-remove-${c}`} aria-label={`Remove ${c}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <form onSubmit={add} className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="PT, ES, FR" className="flex-1 px-3 py-2.5 rounded outline-none text-sm font-mono-vault uppercase" style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }} data-testid="country-input" />
        <button type="submit" className="btn-ghost px-4 py-2 rounded-md text-sm" data-testid="country-add-btn">Add</button>
      </form>
      <div>
        <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Smartphone className="w-3 h-3" /> SMS alert phone</label>
        <input type="tel" value={alertPhone} onChange={(e) => setAlertPhone(e.target.value)} placeholder="+351 912 345 678" className="w-full px-3 py-2.5 rounded outline-none text-sm font-mono-vault" style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }} data-testid="alert-phone-input" />
        <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">Receives a text message whenever a bottle is scanned outside the allowed countries. Leave empty to disable.</p>
      </div>
      <div>
        <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Scan reports email</label>
        <div className="flex gap-2">
          <input type="email" value={summaryEmail} onChange={(e) => setSummaryEmail(e.target.value)} placeholder="you@example.com" className="flex-1 px-3 py-2.5 rounded outline-none text-sm" style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }} data-testid="summary-email-input" />
          <button type="button" onClick={() => sendSummary("weekly")} disabled={sendingSummary || !savedSummary || summaryEmail.trim() !== savedSummary} className="btn-ghost inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm" data-testid="weekly-send-now-btn" title="Send last week's report now">
            <Send className="w-3.5 h-3.5" /> Weekly
          </button>
          <button type="button" onClick={() => sendSummary("daily")} disabled={sendingSummary || !savedSummary || summaryEmail.trim() !== savedSummary} className="btn-ghost inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm" data-testid="summary-send-now-btn" title={summaryEmail.trim() !== savedSummary ? "Save first to send to this address" : "Send yesterday's summary now"}>
            {sendingSummary ? <span className="spin" /> : <Send className="w-3.5 h-3.5" />} Daily
          </button>
        </div>
        <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">Every day at 7:00 (Lisbon): all NFC scans from the day before. Every Monday at 7:00: last week's totals by country and most-scanned bottles. Nothing is sent when there were no scans.</p>
      </div>
      <div className="flex justify-end pt-4 border-t" style={{ borderColor: "var(--vault-border)" }}>
        <button type="button" onClick={save} disabled={saving} className="btn-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm" data-testid="countries-save-btn">
          {saving ? <span className="spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
    </section>
  );
}
